// Reactotron deve ser importado PRIMEIRO para capturar todos os logs no app
// nativo. Na web ele acessa ponte nativa e derruba o bundle em tela branca.
if (__DEV__ && Platform.OS !== 'web') {
  require('./src/config/ReactotronConfig');
}

import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { View, ActivityIndicator, Pressable, Platform } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { NavigationContainer, createNavigationContainerRef } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { enableScreens } from 'react-native-screens';
import { ThemeProvider, useTheme } from './src/contexts/ThemeContext';
import { QuestionsProvider } from './src/contexts/QuestionsContext';
import { AuthProvider, useAuth } from './src/contexts/AuthContext';
import { supabase } from './src/lib/supabase';
import { ChallengeProvider } from './src/contexts/ChallengeContext';
import { SupportProvider } from './src/contexts/SupportContext';
import { QueryProvider } from './src/providers/QueryProvider';
import { FontDisplay, useFonts } from 'expo-font';
import * as SplashScreen from 'expo-splash-screen';
import { captureException, captureMessage } from './src/services/telemetry';
import {
  getSentryInstance,
  registerSentryNavigationContainer,
} from './src/services/telemetrySentry';

// Mantém a splash screen visível até que o app esteja pronto.
// `.catch` evita unhandled rejection caso a API já tenha sido resolvida.
SplashScreen.preventAutoHideAsync().catch(() => {});

// Watchdog de boot: teto absoluto de tempo na splash. Se qualquer await do
// boot travar (fontes, AsyncStorage, etc.), o app entra mesmo assim em vez de
// congelar para sempre. Defesa em profundidade — fail-open.
//
// Em DEV o teto é folgado: o Metro bundla as telas pesadas pré-carregadas sob
// demanda, saturando a thread JS por vários segundos no primeiro boot. Em
// produção o bundle é único/pré-compilado, então 8s é amplo.
const BOOT_TIMEOUT_MS = __DEV__ ? 30000 : 8000;

// Teto para a leitura de sessão que decide a rota inicial. Curto de propósito:
// é só para evitar o flash Home→Onboarding no caso comum. Se estourar, o boot
// segue e o AppNavigator corrige a rota depois. Bem abaixo de BOOT_TIMEOUT_MS.
const AUTH_PROBE_TIMEOUT_MS = 1200;

// Na web, texto precisa aparecer imediatamente com a fonte de sistema enquanto
// a Liebling baixa. Em nativo devolvemos o asset puro e mantemos o contrato do
// expo-font. Declarado fora do componente para nao recriar a funcao no render.
const appFontSource = (asset) => (
  Platform.OS === 'web'
    ? { uri: asset, display: FontDisplay.SWAP }
    : asset
);

// Habilita otimização de telas nativas para melhor performance
enableScreens(true);

// Screens (eager — boot-critical / auth / deep-link entry)
import HomeScreen from './src/screens/HomeScreen';
import LoginScreen from './src/screens/LoginScreen';
import UserAuthScreen from './src/screens/UserAuthScreen';
import ResetPasswordScreen from './src/screens/ResetPasswordScreen';
import OnboardingScreen from './src/screens/OnboardingScreen';
import ProfileScreenEager from './src/screens/ProfileScreen';

// Non-screen components (eager)
import ErrorBoundary from './src/components/ErrorBoundary';
import ScreenErrorBoundary, { withScreenBoundary } from './src/components/ScreenErrorBoundary';
import { AppText } from './src/components/ui/AppText';
import LiveDuelInviteOverlay from './src/components/Duels/LiveDuelInviteOverlay';
// OCULTO P/ LAUNCH (jogos de cassino) — overlay de convite de baralho desligado. Ver decision-log 2026-06-04.
// import LiveCardGameInviteOverlay from './src/components/CardGame/LiveCardGameInviteOverlay';
import OnlinePresenceTracker from './src/components/OnlinePresenceTracker';

const Stack = createStackNavigator();
const navigationRef = createNavigationContainerRef();

// Deep linking configuration for password reset
const linking = {
  prefixes: ['quizflow://'],
  config: {
    screens: {
      UserAuth: 'auth-callback',
      ResetPassword: 'reset-password',
    },
  },
};

// Lazy-loaded screens (deferred to keep boot fast)
// Fundo do tema (não branco): sem cor, o carregamento do chunk lazy piscava
// uma tela BRANCA entre telas escuras (reportado na navegação pós-criar torneio).
const SCREEN_SPINNER_DELAY_MS = 180;
// Na web o chunk de cada tela é um download separado, em rede móvel e às
// vezes com IPv6 capenga (stall de ~5s antes do fallback p/ IPv4 — medido).
// 8s disparava a mensagem "demorando mais que o normal" em carga legítima.
// 15s dá folga sem virar "spinner eterno". Nativo segue 8s (bundle local).
const SCREEN_LOAD_DEADLINE_MS = Platform.OS === 'web' ? 15000 : 8000;

const ScreenFallback = () => {
  const { theme } = useTheme();
  const [phase, setPhase] = useState('quiet'); // quiet -> spinner -> timeout

  // Tres fases, de proposito:
  //  - ate 180ms: fundo liso. O chunk em cache resolve nesse intervalo e um
  //    spinner piscando fica pior que nada.
  //  - 180ms..8s: spinner. Na web cada tela e um download separado, entao a
  //    espera e real e precisa ser sinalizada.
  //  - depois de 8s: mensagem de erro com acao. Carregamento SEM PRAZO e o
  //    que produz "spinner eterno": se a rede pendurar ou o chunk falhar, o
  //    Suspense nunca resolve e a tela mente para o aluno indefinidamente.
  useEffect(() => {
    const toSpinner = setTimeout(() => setPhase('spinner'), SCREEN_SPINNER_DELAY_MS);
    const toTimeout = setTimeout(() => setPhase('timeout'), SCREEN_LOAD_DEADLINE_MS);
    return () => {
      clearTimeout(toSpinner);
      clearTimeout(toTimeout);
    };
  }, []);

  // theme.colors.background — o caminho e `colors`, nao a raiz. Ler
  // `theme.background` devolvia undefined e caia no preto do default, o que
  // pintava a tela de preto num app cujo padrao e o tema claro.
  const colors = theme?.colors ?? {};
  const textColor = colors.text ?? '#1F2937';

  const retry = () => {
    // Recarregar so existe na web. Em nativo o ScreenErrorBoundary ja cobre
    // falha de chunk; aqui o timeout serve como aviso, nao como acao.
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: colors.background ?? '#E8E8E8',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      {phase === 'spinner' ? (
        <ActivityIndicator size="large" color={colors.primary ?? '#8B5CF6'} />
      ) : null}

      {phase === 'timeout' ? (
        <>
          <AppText style={{ color: textColor, fontSize: 16, textAlign: 'center', marginBottom: 4 }}>
            Esta demorando mais que o normal.
          </AppText>
          <AppText style={{ color: textColor, fontSize: 14, opacity: 0.7, textAlign: 'center', marginBottom: 16 }}>
            Verifique sua conexao e tente novamente.
          </AppText>
          {Platform.OS === 'web' ? (
            <Pressable
              onPress={retry}
              style={{
                backgroundColor: colors.primary ?? '#8B5CF6',
                paddingHorizontal: 20,
                paddingVertical: 10,
                borderRadius: 8,
              }}
            >
              <AppText style={{ color: '#FFFFFF', fontSize: 15 }}>Tentar novamente</AppText>
            </Pressable>
          ) : null}
        </>
      ) : null}
    </View>
  );
};
// Cada tela lazy ganha, numa só composição: (1) boundary de rota — contém o
// blast radius de um crash a esta tela; (2) Suspense — fallback enquanto o
// chunk carrega. `name` é derivado de route.name dentro do boundary, sem
// precisar anotar os ~60 call-sites.
const withSuspense = (LazyComponent) => {
  const SuspendedScreen = (props) => (
    <ScreenErrorBoundary navigation={props.navigation} name={props.route?.name}>
      <Suspense fallback={<ScreenFallback />}>
        <LazyComponent {...props} />
      </Suspense>
    </ScreenErrorBoundary>
  );
  return SuspendedScreen;
};

const GamingScreen = withSuspense(lazy(() => import('./src/screens/GamingScreen')));
const CreateQuestionScreen = withSuspense(lazy(() => import('./src/screens/CreateQuestionScreen')));
const ManageQuestionsScreen = withSuspense(lazy(() => import('./src/screens/ManageQuestionsScreen')));
const SelectCategoryScreen = withSuspense(lazy(() => import('./src/screens/SelectCategoryScreen')));
const QuestionSolverScreen = withSuspense(lazy(() => import('./src/screens/QuestionSolverScreen')));
const TimedExamScreen = withSuspense(lazy(() => import('./src/screens/TimedExamScreen')));
const ReviewScheduleScreen = withSuspense(lazy(() => import('./src/screens/ReviewScheduleScreen')));
const SpacedRepetitionScreen = withSuspense(lazy(() => import('./src/screens/SpacedRepetitionScreen')));
const AddSpacedQuestionScreen = withSuspense(lazy(() => import('./src/screens/AddSpacedQuestionScreen')));
const SpacedReviewScreen = withSuspense(lazy(() => import('./src/screens/SpacedReviewScreen')));
const PerformanceScreen = withSuspense(lazy(() => import('./src/screens/PerformanceScreen')));
const PremiumScreen = withSuspense(lazy(() => import('./src/screens/PremiumScreen')));
const CommunityScreen = withSuspense(lazy(() => import('./src/screens/CommunityScreen')));
// Onboarding pós-cadastro (curso/interesses + foto). Lazy como as demais.
const ProfileSetupCourseScreen = withSuspense(lazy(() => import('./src/screens/ProfileSetup/ProfileSetupCourseScreen')));
const ProfileSetupAcademicScreen = withSuspense(lazy(() => import('./src/screens/ProfileSetup/ProfileSetupAcademicScreen')));
const ProfileSetupPhotoScreen = withSuspense(lazy(() => import('./src/screens/ProfileSetup/ProfileSetupPhotoScreen')));
const PostDetailScreen = withSuspense(lazy(() => import('./src/screens/PostDetailScreen')));
// PERFIL É EAGER (não-lazy) DE PROPÓSITO — não reverter para `lazy` sem ler isto.
//
// Medido no aparelho (2026-08-29): com `lazy`, a PRIMEIRA abertura do Perfil
// travava indefinidamente. O chunk baixava (304 em 1ms), a query respondia
// (`status=success`), o componente renderizava INTEIRO — e mesmo assim o
// `ScreenFallback` nunca desmontava. Causa: enquanto o Suspense está pendente,
// o React renderiza a tela FORA DA TELA para preparar o commit; qualquer
// atualização de estado invalida esse trabalho e ele recomeça. Com animações em
// loop rodando a 60fps na web (`useNativeDriver:false`), a invalidação é
// perpétua e o commit nunca acontece. A 2ª tentativa funcionava justamente por
// não haver mais Suspense.
//
// Custo de trazer para o bundle de entrada: ~13,7 KB gzip. Irrelevante perto de
// ser a tela mais usada do app e a que estava quebrada.
const ProfileScreen = withScreenBoundary(ProfileScreenEager, 'Profile');
const FocusModeScreen = withSuspense(lazy(() => import('./src/screens/FocusModeScreen')));
const ConcursosScreen = withSuspense(lazy(() => import('./src/screens/ConcursosScreen')));
const SimuladoBancaScreen = withSuspense(lazy(() => import('./src/screens/SimuladoBancaScreen')));
const StudyStreakScreen = withSuspense(lazy(() => import('./src/screens/StudyStreakScreen')));
const ChallengesScreen = withSuspense(lazy(() => import('./src/screens/ChallengesScreen')));
const ChallengeParticipantsScreen = withSuspense(lazy(() => import('./src/screens/ChallengeParticipantsScreen')));
const CreateChallengeScreen = withSuspense(lazy(() => import('./src/screens/CreateChallengeScreen')));
const SecondBrainScreen = withSuspense(lazy(() => import('./src/screens/SecondBrainScreen')));
const ShortTermInsightsScreen = withSuspense(lazy(() => import('./src/screens/ShortTermInsightsScreen')));
const ShortTermMemoryScreen = withSuspense(lazy(() => import('./src/screens/ShortTermMemoryScreen')));
const ShortTermPracticeScreen = withSuspense(lazy(() => import('./src/screens/ShortTermPracticeScreen')));
const MediumTermInsightsScreen = withSuspense(lazy(() => import('./src/screens/MediumTermInsightsScreen')));
const LongTermInsightsScreen = withSuspense(lazy(() => import('./src/screens/LongTermInsightsScreen')));
const MediumTermMemoryScreen = withSuspense(lazy(() => import('./src/screens/MediumTermMemoryScreen')));
const LongTermMemoryScreen = withSuspense(lazy(() => import('./src/screens/LongTermMemoryScreen')));
const DuelsScreen = withSuspense(lazy(() => import('./src/screens/Duels/DuelsScreen')));
const FindOpponentScreen = withSuspense(lazy(() => import('./src/screens/Duels/FindOpponentScreen')));
const DuelWaitingRoomScreen = withSuspense(lazy(() => import('./src/screens/Duels/DuelWaitingRoomScreen')));
const DuelMatchScreen = withSuspense(lazy(() => import('./src/screens/DuelMatch/DuelMatchScreen')));
// OCULTO P/ LAUNCH (jogos de cassino) — ver rotas comentadas abaixo + decision-log 2026-06-04.
// const CardGameLobbyScreen = withSuspense(lazy(() => import('./src/screens/CardGame/CardGameLobbyScreen')));
// const CardGameFindOpponentScreen = withSuspense(lazy(() => import('./src/screens/CardGame/CardGameFindOpponentScreen')));
// const CardGameWaitingRoomScreen = withSuspense(lazy(() => import('./src/screens/CardGame/CardGameWaitingRoomScreen')));
// const CardGameMatchScreen = withSuspense(lazy(() => import('./src/screens/CardGame/CardGameMatchScreen')));
// const Blackjack21LobbyScreen = withSuspense(lazy(() => import('./src/screens/Blackjack21/Blackjack21LobbyScreen')));
// const Blackjack21TableScreen = withSuspense(lazy(() => import('./src/screens/Blackjack21/Blackjack21TableScreen')));
const PublicTendersScreen = withSuspense(lazy(() => import('./src/screens/PublicTendersScreen')));
const UserProfileScreen = withSuspense(lazy(() => import('./src/screens/UserProfileScreen')));
const BlockedUsersScreen = withSuspense(lazy(() => import('./src/screens/BlockedUsersScreen')));
const TournamentHubScreen = withSuspense(lazy(() => import('./src/screens/TournamentHubScreen')));
const BracketScreen = withSuspense(lazy(() => import('./src/screens/BracketScreen')));
const MatchScreen = withSuspense(lazy(() => import('./src/screens/MatchScreen')));
const ChampionsHistoryScreen = withSuspense(lazy(() => import('./src/screens/ChampionsHistoryScreen')));
const MyTournamentStatsScreen = withSuspense(lazy(() => import('./src/screens/MyTournamentStatsScreen')));
const TournamentLeaderboardScreen = withSuspense(lazy(() => import('./src/screens/TournamentLeaderboardScreen')));
const WaitingRoomScreen = withSuspense(lazy(() => import('./src/screens/WaitingRoomScreen')));
const WarmupScreen = withSuspense(lazy(() => import('./src/screens/WarmupScreen')));
const TournamentRulesScreen = withSuspense(lazy(() => import('./src/screens/TournamentRulesScreen')));
const TournamentCalendarScreen = withSuspense(lazy(() => import('./src/screens/TournamentCalendarScreen')));
const SpectatorScreen = withSuspense(lazy(() => import('./src/screens/SpectatorScreen')));
const CreateTournamentScreen = withSuspense(lazy(() => import('./src/screens/CreateTournamentScreen')));
const TournamentDetailsScreen = withSuspense(lazy(() => import('./src/screens/TournamentDetailsScreen')));
const TournamentResultScreen = withSuspense(lazy(() => import('./src/screens/TournamentResultScreen')));
const CompetitorBenchmarkScreen = withSuspense(lazy(() => import('./src/screens/benchmark/CompetitorBenchmarkScreen')));
/* Corretor de Redacao OCULTO (2026-08-30) — ver nota nas rotas, mais abaixo.
const RedacaoScreen = withSuspense(lazy(() => import('./src/screens/Redacao/RedacaoScreen')));
const RedacaoResultScreen = withSuspense(lazy(() => import('./src/screens/Redacao/RedacaoResultScreen')));
const RedacaoHistoricoScreen = withSuspense(lazy(() => import('./src/screens/Redacao/RedacaoHistoricoScreen')));
const RedacaoDetalhesScreen = withSuspense(lazy(() => import('./src/screens/Redacao/RedacaoDetalhesScreen')));
*/

// Telas eager também recebem boundary de rota (o nome vem de route.name).
const HomeScreenSafe = withScreenBoundary(HomeScreen);
const LoginScreenSafe = withScreenBoundary(LoginScreen);
const UserAuthScreenSafe = withScreenBoundary(UserAuthScreen);
const ResetPasswordScreenSafe = withScreenBoundary(ResetPasswordScreen);
const OnboardingScreenSafe = withScreenBoundary(OnboardingScreen);

// Rotas que NÃO exigem login. Tudo o que não estiver aqui é gated.
//
// 'Home' SAIU daqui em 2026-08-05: deslogado não navega mais pelo app, entra
// pela tela de boas-vindas. As que sobraram são as portas de entrada de auth —
// 'ResetPassword' e 'UserAuth' precisam continuar públicas porque são alvo de
// deep link (`quizflow://reset-password` e `auth-callback`), abertos justamente
// por quem está deslogado.
const PUBLIC_ROUTES = new Set([
  'Onboarding',
  'UserAuth',
  'Login',
  'ResetPassword',
]);

// Rotas do onboarding de perfil — isentas da trava abaixo, senão ela se
// redirecionaria para si mesma em laço.
const PROFILE_SETUP_ROUTES = new Set(['ProfileSetupCourse', 'ProfileSetupPhoto']);

function AppNavigator({ initialRoute }) {
  const { theme, isDarkMode } = useTheme();
  const { isAuthenticated, initialized, profile, profileStatus } = useAuth();

  // Guard centralizado: ao focar qualquer rota, valida se exige login e
  // redireciona para UserAuth carregando o returnTo. Evita acoplar a regra
  // em cada tela individual. screenListeners aceita uma factory que recebe
  // ({ navigation, route }) e retorna o mapa de listeners — esse é o caminho
  // suportado em React Navigation v6/v7 para acessar a rota atual.
  const screenListeners = React.useCallback(({ navigation, route }) => ({
    focus: () => {
      if (!initialized) return; // aguarda restauração de sessão (cold start)

      if (!isAuthenticated) {
        if (PUBLIC_ROUTES.has(route.name)) return;
        navigation.reset({
          index: 0,
          routes: [{ name: 'UserAuth', params: { returnTo: route.name } }],
        });
        return;
      }

      // ── Trava de onboarding ────────────────────────────────────────────────
      // Quem ainda não informou o curso é levado ao fluxo de configuração. Cobre
      // três casos que o gancho pós-cadastro não alcança: quem fechou o app no
      // meio, quem tocou em "continuar mesmo assim" após falha de rede, e quem
      // já tinha conta antes desta funcionalidade existir.
      //
      // ⚠️ REGRA DE SEGURANÇA: só intercepta com o perfil COMPROVADAMENTE
      // carregado. Se ele falhou, está carregando ou não foi encontrado, o
      // `curso` é DESCONHECIDO — e tratar desconhecido como ausente prenderia
      // TODOS os usuários no onboarding sempre que o perfil não carregasse.
      // Interceptar só no `loaded` faz a falha ser inofensiva (o app abre
      // normal) em vez de catastrófica.
      if (profileStatus !== 'loaded') return;
      if (profile?.curso) return;
      if (PROFILE_SETUP_ROUTES.has(route.name)) return;
      if (PUBLIC_ROUTES.has(route.name)) return;

      navigation.reset({
        index: 0,
        routes: [{ name: 'ProfileSetupCourse', params: { returnTo: route.name } }],
      });
    },
  }), [isAuthenticated, initialized, profile?.curso, profileStatus]);

  // Correção pós-boot (one-shot). O boot decide a rota inicial com um teto de
  // AUTH_PROBE_TIMEOUT_MS; se a leitura de sessão não respondeu a tempo, ele cai
  // em 'Home'. Aqui, assim que a sessão de verdade chega, um usuário deslogado
  // que tenha caído numa rota gated é mandado para a tela de boas-vindas.
  //
  // Por que não basta o `screenListeners.focus`: o focus da rota INICIAL dispara
  // no mount, quando `initialized` ainda é false — e ele não volta a rodar sem
  // uma troca de tela.
  //
  // Rotas públicas são preservadas de propósito: sem isso, abrir o app por
  // `quizflow://reset-password` (usuário deslogado, por definição) seria
  // sequestrado para o Onboarding e a troca de senha nunca aconteceria.
  const coldStartFixedRef = React.useRef(false);
  React.useEffect(() => {
    if (!initialized || coldStartFixedRef.current) return;
    // isReady() antes de consumir o one-shot: se o container ainda não montou,
    // não queima a única tentativa — degrada para o guard de foco de sempre.
    if (!navigationRef.isReady()) return;
    coldStartFixedRef.current = true;
    if (isAuthenticated) return;
    const currentRoute = navigationRef.getCurrentRoute?.()?.name;
    if (!currentRoute || PUBLIC_ROUTES.has(currentRoute)) return;
    navigationRef.reset({ index: 0, routes: [{ name: 'Onboarding' }] });
  }, [initialized, isAuthenticated]);

  return (
    <>
      <StatusBar style={isDarkMode ? 'light' : 'dark'} />
      <Stack.Navigator
        initialRouteName={initialRoute}
        screenListeners={screenListeners}
        screenOptions={{
          headerShown: false,
          cardStyle: {
            backgroundColor: isDarkMode ? '#0B0D17' : '#F5F7FA',
            // WEB — o card do Stack fica `flex: 0 0 auto` no navegador, ou seja,
            // com a ALTURA DO CONTEÚDO em vez da altura da viewport. Medido com
            // getComputedStyle: numa viewport de 850px o card da Home ficava com
            // 1531px. Consequências em cascata, em TODA tela:
            //   · o `flex: 1` da raiz de cada tela herda essa altura inflada;
            //   · o ScrollView interno nunca rola (ele já "cabe" no card gigante);
            //   · o `body { overflow: hidden }` do reset do Expo corta o excedente
            //     sem barra de rolagem — só a primeira dobra fica alcançável;
            //   · quem é `position:absolute; bottom:0` (BottomTabBar) ancora no pé
            //     do card, não no da tela — ia parar em y=1466, fora de vista.
            // `flex: 1` devolve o card à altura da viewport e desfaz os quatro.
            // Só na web: em nativo o card já se comporta assim, e o combinado é
            // não alterar o visual do app.
            ...(Platform.OS === 'web' ? { flex: 1 } : null),
          },
          // Navegação INSTANTÂNEA - React Navigation v7
          animation: 'none',
          gestureEnabled: false,
        }}
      >
        <Stack.Screen name="Home" component={HomeScreenSafe} />
        <Stack.Screen name="Gaming" component={GamingScreen} />
        <Stack.Screen
          name="CreateQuestion"
          component={CreateQuestionScreen}
        />
        <Stack.Screen
          name="ManageQuestions"
          component={ManageQuestionsScreen}
        />
        <Stack.Screen
          name="SelectCategory"
          component={SelectCategoryScreen}
        />
        <Stack.Screen
          name="QuestionSolver"
          component={QuestionSolverScreen}
          options={{ gestureEnabled: false }}
        />
        <Stack.Screen
          name="TimedExam"
          component={TimedExamScreen}
          options={{ gestureEnabled: false }}
        />
        <Stack.Screen
          name="ReviewSchedule"
          component={ReviewScheduleScreen}
        />
        <Stack.Screen
          name="SpacedRepetition"
          component={SpacedRepetitionScreen}
        />
        <Stack.Screen
          name="AddSpacedQuestion"
          component={AddSpacedQuestionScreen}
        />
        <Stack.Screen
          name="SpacedReview"
          component={SpacedReviewScreen}
        />
        <Stack.Screen
          name="Performance"
          component={PerformanceScreen}
        />
        <Stack.Screen
          name="CompetitorBenchmark"
          component={CompetitorBenchmarkScreen}
        />
        <Stack.Screen
          name="StudyStreak"
          component={StudyStreakScreen}
        />
        {/* Premium/planos OCULTO no UniQuest (2026-08-03): o app é gratuito para os alunos,
            custeado por contrato institucional. Tela exibia preços em R$ sem meio de pagamento
            (não há lib de IAP no projeto) — confunde o aluno e chama atenção da revisão das lojas.
            Arquivo mantido intacto; reativar = descomentar. Mesmo padrão de Mesa 21 / Jogo de Cartas.
        <Stack.Screen
          name="Premium"
          component={PremiumScreen}
        />
        */}
        <Stack.Screen
          name="Community"
          component={CommunityScreen}
        />
<Stack.Screen
          name="PostDetail"
          component={PostDetailScreen}
        />
        <Stack.Screen
          name="Profile"
          component={ProfileScreen}
        />
        <Stack.Screen
          name="FocusMode"
          component={FocusModeScreen}
        />
        <Stack.Screen
          name="Login"
          component={LoginScreenSafe}
        />
        <Stack.Screen
          name="Concursos"
          component={ConcursosScreen}
        />
        <Stack.Screen
          name="SimuladoBanca"
          component={SimuladoBancaScreen}
        />
        <Stack.Screen
          name="Challenges"
          component={ChallengesScreen}
        />
        <Stack.Screen
          name="ChallengeParticipants"
          component={ChallengeParticipantsScreen}
        />
        <Stack.Screen
          name="CreateChallenge"
          component={CreateChallengeScreen}
        />
        <Stack.Screen
          name="UserAuth"
          component={UserAuthScreenSafe}
        />
        <Stack.Screen
          name="ProfileSetupCourse"
          component={ProfileSetupCourseScreen}
        />
        <Stack.Screen
          name="ProfileSetupAcademic"
          component={ProfileSetupAcademicScreen}
        />
        <Stack.Screen
          name="ProfileSetupPhoto"
          component={ProfileSetupPhotoScreen}
        />
        <Stack.Screen
          name="SecondBrain"
          component={SecondBrainScreen}
        />
        <Stack.Screen
          name="ShortTermInsights"
          component={ShortTermInsightsScreen}
        />
        <Stack.Screen
          name="ShortTermMemory"
          component={ShortTermMemoryScreen}
        />
        <Stack.Screen
          name="ShortTermPractice"
          component={ShortTermPracticeScreen}
          options={{ gestureEnabled: false }}
        />
        <Stack.Screen
          name="MediumTermInsights"
          component={MediumTermInsightsScreen}
        />
        <Stack.Screen
          name="LongTermInsights"
          component={LongTermInsightsScreen}
        />
        <Stack.Screen
          name="MediumTermMemory"
          component={MediumTermMemoryScreen}
        />
        <Stack.Screen
          name="LongTermMemory"
          component={LongTermMemoryScreen}
        />
        <Stack.Screen
          name="ResetPassword"
          component={ResetPasswordScreenSafe}
        />
        <Stack.Screen
          name="Duels"
          component={DuelsScreen}
        />
        <Stack.Screen
          name="FindOpponent"
          component={FindOpponentScreen}
        />
        <Stack.Screen
          name="DuelMatch"
          component={DuelMatchScreen}
          options={{ gestureEnabled: false }}
        />
        <Stack.Screen
          name="DuelWaitingRoom"
          component={DuelWaitingRoomScreen}
          options={{ gestureEnabled: false }}
        />
        {/* OCULTO P/ LAUNCH (jogos de cassino — classificação IARC sem jogo de azar).
            Rotas removidas p/ não serem alcançáveis nem por código/deep link. Telas e
            features intactas. Reverter = descomentar + flag SHOW_CASINO_GAMES no GamingScreen.
            Ver decision-log 2026-06-04.
        <Stack.Screen
          name="CardGameLobby"
          component={CardGameLobbyScreen}
        />
        <Stack.Screen
          name="CardGameFindOpponent"
          component={CardGameFindOpponentScreen}
        />
        <Stack.Screen
          name="CardGameWaitingRoom"
          component={CardGameWaitingRoomScreen}
          options={{ gestureEnabled: false }}
        />
        <Stack.Screen
          name="CardGameMatch"
          component={CardGameMatchScreen}
          options={{ gestureEnabled: false }}
        />
        <Stack.Screen
          name="Blackjack21Lobby"
          component={Blackjack21LobbyScreen}
        />
        <Stack.Screen
          name="Blackjack21Table"
          component={Blackjack21TableScreen}
          options={{ gestureEnabled: false }}
        />
        */}
        <Stack.Screen
          name="PublicTenders"
          component={PublicTendersScreen}
        />
        <Stack.Screen
          name="UserProfile"
          component={UserProfileScreen}
        />
        <Stack.Screen
          name="BlockedUsers"
          component={BlockedUsersScreen}
        />
        <Stack.Screen
          name="TournamentHub"
          component={TournamentHubScreen}
        />
        <Stack.Screen
          name="CreateTournament"
          component={CreateTournamentScreen}
        />
        <Stack.Screen
          name="TournamentDetails"
          component={TournamentDetailsScreen}
        />
        <Stack.Screen
          name="BracketScreen"
          component={BracketScreen}
        />
        <Stack.Screen
          name="MatchScreen"
          component={MatchScreen}
          options={{ gestureEnabled: false }}
        />
        <Stack.Screen
          name="ChampionsHistory"
          component={ChampionsHistoryScreen}
        />
        <Stack.Screen
          name="MyTournamentStats"
          component={MyTournamentStatsScreen}
        />
        <Stack.Screen
          name="TournamentLeaderboard"
          component={TournamentLeaderboardScreen}
        />
        <Stack.Screen
          name="WaitingRoom"
          component={WaitingRoomScreen}
          options={{ gestureEnabled: false }}
        />
        <Stack.Screen
          name="Warmup"
          component={WarmupScreen}
        />
        <Stack.Screen
          name="TournamentRules"
          component={TournamentRulesScreen}
        />
        <Stack.Screen
          name="TournamentCalendar"
          component={TournamentCalendarScreen}
        />
        <Stack.Screen
          name="TournamentResult"
          component={TournamentResultScreen}
        />
        <Stack.Screen
          name="Spectator"
          component={SpectatorScreen}
        />
        <Stack.Screen
          name="Onboarding"
          component={OnboardingScreenSafe}
          options={{ headerShown: false }}
        />
        {/* Corretor de Redacao OCULTO no UniQuest (2026-08-30): a funcionalidade nao faz parte
            do escopo do ENADE. Motivo adicional de seguranca: o fluxo enviava
            EXPO_PUBLIC_QUIZFLOW_WEBHOOK_TOKEN, que ia embutido no bundle publico e portanto
            era legivel por qualquer pessoa, permitindo abuso da cota Gemini do n8n.
            Arquivos mantidos intactos; reativar = descomentar E resolver a autenticacao
            do webhook no servidor. Mesmo padrao de Premium / Mesa 21 / Jogo de Cartas.
        <Stack.Screen
          name="Redacao"
          component={RedacaoScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="RedacaoResult"
          component={RedacaoResultScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="RedacaoHistorico"
          component={RedacaoHistoricoScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="RedacaoDetalhes"
          component={RedacaoDetalhesScreen}
          options={{ headerShown: false }}
        />
        */}
      </Stack.Navigator>
    </>
  );
}

export default function App() {
  // `fontError` NÃO pode ser descartado: se uma fonte falhar (download
  // corrompido, OOM, disco cheio), `fontsLoaded` nunca vira true e a splash
  // congelaria para sempre. Tratamos o erro como "fontes prontas" (fallback
  // para a fonte do sistema) — estética não deve bloquear disponibilidade.
  const [fontsLoaded, fontError] = useFonts({
    'Liebling': appFontSource(require('./assets/fonts/Liebling.otf')),
    'Liebling-Light': appFontSource(require('./assets/fonts/LieblingLight.otf')),
    'Liebling-Medium': appFontSource(require('./assets/fonts/LieblingMedium.otf')),
    'Liebling-Bold': appFontSource(require('./assets/fonts/LieblingBold.otf')),
    'Liebling-Heavy': appFontSource(require('./assets/fonts/LieblingHeavy.otf')),
  });

  const [initialRoute, setInitialRoute] = useState(null);
  const [bootTimedOut, setBootTimedOut] = useState(false);

  // Espelhos do estado de boot lidos pelo watchdog no momento em que ele dispara
  // (o efeito tem deps vazias p/ manter o teto absoluto a partir do mount, então
  // não pode ler `bootReady`/`fontsReady`/`initialRoute` direto — seriam o valor
  // congelado do 1º render). Refs dão o valor ATUAL no instante do timeout.
  const bootReadyRef = useRef(false);
  const fontsReadyRef = useRef(false);
  const initialRouteRef = useRef(null);

  // WEB: FontFace pode levar varios segundos em 3G/4G ruim. Esperar as sete
  // familias aqui mantinha a SPA inteira em branco ate o watchdog de 8s, mesmo
  // com React, sessao e dados prontos. O browser ja faz font-display/fallback e
  // troca a fonte quando o download termina, portanto tipografia nao faz parte
  // do caminho critico no web. O splash nativo preserva o comportamento atual.
  const fontsReady = Platform.OS === 'web' || fontsLoaded || !!fontError;

  // Decide a rota inicial pela SESSÃO: deslogado entra pela tela de boas-vindas,
  // logado vai direto para a Home.
  //
  // Por que uma corrida com timeout, e não um `await` simples: `getSession()` lê
  // do AsyncStorage (rápido), MAS com `autoRefreshToken: true` ele vai à REDE
  // quando o token está expirado. Numa rede que descarta pacote isso penduraria
  // a splash. Então damos um teto curto: se não responder a tempo, caímos no
  // comportamento antigo ('Home') e o `AppNavigator` corrige a rota assim que
  // `initialized` chegar (ver useEffect de correção pós-boot lá).
  //
  // try/catch obrigatório: nada aqui pode deixar `initialRoute` em null — isso
  // prenderia o app na splash até o watchdog (brick de boot).
  useEffect(() => {
    let active = true;
    let timer = null;

    async function decideInitialRoute() {
      try {
        // `.catch` no probe evita unhandled rejection caso ele falhe DEPOIS de o
        // timeout já ter vencido a corrida.
        const probe = supabase.auth
          .getSession()
          .then(({ data }) => (data?.session ? 'Home' : 'Onboarding'))
          .catch(() => null);
        const timeout = new Promise((resolve) => {
          timer = setTimeout(() => resolve(null), AUTH_PROBE_TIMEOUT_MS);
        });
        const decided = await Promise.race([probe, timeout]);
        if (active) setInitialRoute(decided ?? 'Home');
      } catch (error) {
        captureException(error, { scope: 'boot', step: 'decideInitialRoute' });
        if (active) setInitialRoute('Home');
      }
    }

    decideInitialRoute();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Watchdog: garante saída da splash mesmo se algo do boot travar.
  // Rede de segurança (não é crash) → severidade de warning, evitando
  // poluir a telemetria de erro em boots lentos-porém-bem-sucedidos.
  useEffect(() => {
    const timer = setTimeout(() => {
      // Boot já concluiu dentro do teto → nada de alarme. Sem este guard o
      // timer dispara em TODA sessão (o efeito só limpa no unmount, que não
      // ocorre), poluindo o Sentry com um "timeout" falso a cada abertura.
      if (bootReadyRef.current) return;
      setBootTimedOut(true);
      captureMessage('Boot watchdog timeout', {
        scope: 'boot',
        fontsReady: fontsReadyRef.current,
        hasInitialRoute: initialRouteRef.current !== null,
      });
    }, BOOT_TIMEOUT_MS);
    return () => clearTimeout(timer);
    // Intencionalmente sem deps: o teto é absoluto a partir do mount; o callback
    // lê o estado atual via refs, então não há deps a declarar.
  }, []);

  const bootReady = (fontsReady && initialRoute !== null) || bootTimedOut;

  // WEB — completa o pré-carregamento da barra inferior. O bloco mais abaixo
  // (runAfterInteractions) já aquece Gaming/Community/Profile nas DUAS
  // plataformas; faltavam Desempenho, Questões e Desafios, que na web são
  // downloads separados (chunks). Só web e só após o boot, em idle, para não
  // disputar rede com o carregamento inicial. Fire-and-forget: falha aqui não
  // afeta nada (o import real da rota tenta de novo). Em nativo o bundle é
  // único — avaliar 3 módulos a mais no boot seria custo sem ganho, então
  // nem entra.
  useEffect(() => {
    if (Platform.OS !== 'web' || !bootReady) return undefined;
    const warm = () => {
      import('./src/screens/PerformanceScreen').catch(() => {});
      import('./src/screens/ConcursosScreen').catch(() => {});
      import('./src/screens/ChallengesScreen').catch(() => {});
    };
    const idle = typeof window !== 'undefined' && window.requestIdleCallback;
    const handle = idle ? window.requestIdleCallback(warm, { timeout: 4000 }) : setTimeout(warm, 2500);
    return () => (idle ? window.cancelIdleCallback(handle) : clearTimeout(handle));
  }, [bootReady]);
  // Mantém os espelhos do watchdog em dia a cada render.
  bootReadyRef.current = bootReady;
  fontsReadyRef.current = fontsReady;
  initialRouteRef.current = initialRoute;
  // Em timeout sem rota resolvida, cai em 'Home' (pública) como fallback.
  const resolvedInitialRoute = initialRoute ?? 'Home';

  // Esconde a splash quando o boot está pronto (ou estourou o watchdog).
  //
  // WEB: aqui também sai o **shell de boot** (o bloco `#boot-shell` do
  // `public/index.html`). Ele existe porque este componente retorna `null` até
  // `bootReady` — sem shell, a web pintava BRANCO durante todo o download +
  // parse do bundle e mais a sondagem de sessão. É HTML+CSS puro, então aparece
  // no parse do documento, sem esperar JS.
  //
  // A saída é por classe (fade de 180ms) e só então `remove()`, para não haver
  // corte seco entre o shell e a primeira tela. Se qualquer coisa falhar antes
  // daqui, o próprio `index.html` troca o shell para um estado de erro com
  // "Recarregar" passados 12s — teto acima do BOOT_TIMEOUT_MS, para não
  // competir com um boot lento-porém-bem-sucedido.
  useEffect(() => {
    if (!bootReady) return undefined;
    SplashScreen.hideAsync().catch(() => {});

    if (Platform.OS !== 'web' || typeof document === 'undefined') return undefined;
    const shell = document.getElementById('boot-shell');
    if (!shell) return undefined;
    shell.classList.add('is-leaving');
    const timer = setTimeout(() => shell.remove(), 200);
    return () => clearTimeout(timer);
  }, [bootReady]);

  // Pré-carrega (em idle, após o boot) os módulos das telas pesadas mais
  // prováveis, evitando o atraso de avaliação do módulo na primeira navegação.
  // Apenas avalia o módulo — não monta nem dispara fetches.
  useEffect(() => {
    // NÃO usar InteractionManager.runAfterInteractions aqui.
    //
    // Medido: ele NUNCA dispara nesta aplicação. `Animated.timing` marca
    // `isInteraction: true` por padrão, criando um bloqueio que só é liberado
    // quando a animação TERMINA — e um `Animated.loop` nunca termina. O app tem
    // 48 loops; basta um card decorativo montar para o InteractionManager ficar
    // preso pelo resto da sessão, e o pré-aquecimento nunca acontecer (isso
    // valia para o app NATIVO também, silenciosamente).
    // Um timer simples não depende de nada disso.
    // IMEDIATO, não em idle. Estas telas são destinos primários e continuam
    // `lazy` — logo, se o usuário tocar nelas ANTES de o módulo chegar, o
    // Suspense suspende e a tela pode travar pelo mesmo motivo que travava o
    // Perfil (render fora da tela invalidado a 60fps pelas animações em loop;
    // ver a nota no ProfileScreen eager acima). Adiar o pré-carregamento é
    // justamente o que abre essa janela.
    //
    // Custo de começar já: os dois chunks somam ~82 KB gzip e baixam em
    // paralelo com o resto — não bloqueiam a primeira pintura, que agora é do
    // shell estático no `index.html`.
    import('./src/screens/GamingScreen').catch(() => {});
    import('./src/screens/CommunityScreen').catch(() => {});
    return undefined;
  }, []);

  // Mantém a splash nativa visível até o boot estar pronto (ou o watchdog estourar).
  if (!bootReady) {
    return null;
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <ErrorBoundary>
        <QueryProvider>
          <AuthProvider>
            <ThemeProvider>
              <ChallengeProvider>
                <SupportProvider>
                  <QuestionsProvider>
                    <NavigationContainer
                      ref={navigationRef}
                      linking={linking}
                      onReady={() => {
                        // Registra a tela inicial e as navegações como transactions.
                        registerSentryNavigationContainer(navigationRef);
                      }}
                      onStateChange={() => {
                        // A cada troca de tela, cria um breadcrumb de navegação.
                        const currentRoute = navigationRef.getCurrentRoute?.();
                        if (currentRoute?.name) {
                          const Sentry = getSentryInstance();
                          Sentry?.addBreadcrumb?.({
                            category: 'navigation',
                            message: `→ ${currentRoute.name}`,
                            level: 'info',
                            data: { params: currentRoute.params },
                          });
                        }
                      }}
                    >
                      <OnlinePresenceTracker />
                      <AppNavigator initialRoute={resolvedInitialRoute} />
                      <LiveDuelInviteOverlay navigationRef={navigationRef} />
                      {/* OCULTO P/ LAUNCH (cassino): <LiveCardGameInviteOverlay navigationRef={navigationRef} /> */}
                    </NavigationContainer>
                  </QuestionsProvider>
                </SupportProvider>
              </ChallengeProvider>
            </ThemeProvider>
          </AuthProvider>
        </QueryProvider>
      </ErrorBoundary>
    </GestureHandlerRootView>
  );
}
